import { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchDocument, findDocuments, selectDocument } from "../common/slices/document";
import { fetchFolders } from "../common/slices/folder";
import Layout, { Content } from "./Layout";
import PDFPreview from "./PDFPreview";
import SidebarDocument from "./SidebarDocument";
import SidebarDocumentList from "./SidebarDocumentList";
import { pathNames } from "../constants";
import { selectActive, selectActiveLocation } from "../common/slices/location";
import { toast } from "react-toastify";
import { ErrorContext } from "../ErrorContext";
import { H1, P } from "./Typography";
import ActionContainer from "./ActionContainer";
import { StyledSearch } from "./Search";
import FormFilterDropDown from "./FormFilterDropDown";
import { useForm, useWatch } from "react-hook-form";
import FormSelectCustomer from "./FormSelectCustomer";
import FormDatePicker from "./FormDatePicker";
import moment from 'moment';
import { fetchCustomerMedia, loadNextPage, loadPreviousPage, resetPagination, selectHasNextPage, selectHasPreviousPage, selectMedia, setMedia } from "../common/slices/media";
import S3Image from "./S3Image";
import { TableFooter } from "./Table";
import { BlackButton, ButtonBase } from "./Buttons";
import { FaChevronLeft, FaChevronRight, FaSpinner } from "react-icons/fa";
import Row from "./Row";
import styled from "styled-components";
import theme from "../common/theme";
import PreviewDailyReport from "./ScreenPreviewDailyReport";
import PreviewImage from "./ScreenPreviewImage";

const ImageContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
    width: 100%;
    margin-bottom: 1rem;    
`

const ImageWrapper = styled.div`
    width: 100%;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    background: ${theme.palette.light};
    border: 1px solid ${theme.palette.border};

    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
    cursor: pointer;

    img {
        width: 100%;
        height: calc(100% - 2rem);
        object-fit: cover;
        display: block;
        transition: transform 0.2s ease;
    }

    &:hover {
        border-color: ${theme.palette.primary};
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:hover img {
        transform: scale(1.05);
    }
`

const Images = () => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const nav = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const search = searchParams.get("search") || "";
    const document = useSelector(selectDocument);
    const location = useSelector(selectActiveLocation);
    const media = useSelector(selectMedia);
    const hasNextPage = useSelector(selectHasNextPage)
    const hasPrevPage = useSelector(selectHasPreviousPage)

    const { control, watch, handleSubmit, formState: { errors, isValid, isSubmitting } } = useForm({
        defaultValues: {
            startDate: moment().add(-2, 'w').startOf('d'),
            endDate: moment().endOf('d'),
        }
    })
    const fieldProps = { control, errors, rules: { required: true } }

    const [customerId, startDate, endDate] = useWatch({
        control,
        name: [
            "customerId",
            "startDate",
            "endDate",
        ],
    });

    useEffect(() => {
        return () => dispatch(setMedia({ items: [], nextToken: null }))
    }, [])

    useEffect(() => {
        if (!isValid) return;
        const timeout = setTimeout(getMedia, 400);
        return () => clearTimeout(timeout);
    }, [isValid, customerId, startDate, endDate, location?.id]);

    const getMedia = async () => {
        try {
            dispatch(resetPagination());
            await dispatch(fetchCustomerMedia(startDate, endDate, customerId?.value, location.id))
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to fetch media", forSupport: ex.message })
        }
    };

    const nextPage = (values) =>
        dispatch(loadNextPage({
            ...values,
            customerId: values.customerId?.value,
        }))

    const prevPage = (values) =>
        dispatch(loadPreviousPage({
            ...values,
            customerId: values.customerId?.value,
        }))

    return (
        <Layout>
            <H1 style={{ marginTop: '2rem' }}>Images</H1>
            <ActionContainer>
                <div style={{ flex: 1 }} />
                <FormSelectCustomer {...fieldProps} name="customerId" label="Customer" placeholder="Select a Customer" locationId={location?.id} />
                <FormDatePicker {...fieldProps} name="startDate" label="Start Date" placeholder="Select a Start Date" />
                <FormDatePicker {...fieldProps} name="endDate" label="End Date" placeholder="Select a End Date" />
            </ActionContainer>

            <ImageContainer>
                {
                    media.map(m => (
                        <ImageWrapper key={m.id} onClick={() => nav(`/images/${m.id}`)}>
                            <S3Image imgKey={m.filename} />
                            <P style={{ padding: '0.5rem 0 0 0.5rem' }}>{moment(m.createdAt).format('YYYY-MM-DD')}</P>
                        </ImageWrapper>
                    ))
                }
            </ImageContainer>
            {
                media.length === 0 && <P style={{ padding: '1rem' }}>No images for the selected customer.  Please adjust your selection and try again.</P>
            }

            {
                id && <PreviewImage id={id} />
            }

            <TableFooter>
                {isSubmitting && <FaSpinner className='fa-spin' />}
                <ButtonBase onClick={handleSubmit(prevPage)} style={{ color: 'black' }} disabled={!hasPrevPage || !isValid} ><FaChevronLeft /></ButtonBase>
                <ButtonBase onClick={handleSubmit(nextPage)} style={{ color: 'black' }} disabled={!hasNextPage || !isValid}><FaChevronRight /></ButtonBase>
            </TableFooter>
        </Layout>
    );
};
export default Images;
